// Section 25 (M5d): the steward's answer to every prompt kind. Shortage: accept the policy's
// proposal and eat seed only under starvation. Events: a sensible fixed choice per event id,
// falling back to the prompt's default. Arrivals (event prompts with a group): the migration
// policy, and under "ask each time" admit when beds and food allow. Justice: the policy's
// response. Pairing: approve. Pure and deterministic; project() uses it for open prompts.
import { config, type Config } from '../../config';
import { registryFor } from '../../content';
import { isChild, isWorking, peopleSorted, settlementTrust } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type {
  ArrivalDecision,
  EventId,
  EventPrompt,
  GameState,
  Person,
  PersonId,
  Prompt,
  PromptAnswer,
  ShortagePrompt,
} from '../../core/types';

/** Preferred choice ids per event; used when the prompt offers them. */
const PREFERRED: Partial<Record<EventId, readonly string[]>> = {
  blight: ['burn-fields'],
  'birth-complication': ['assign-healer'],
  discovery: ['develop'],
  'fever-outbreak': ['quarantine'],
  'elders-request': ['grant'],
  'improvement-proposal': ['commit'],
  drought: ['dig-deeper'],
  plague: ['quarantine'],
  'wild-animals': ['avoid'],
  rumour: ['address'],
  'trade-caravan': ['decline'],
};

/** Choice ids that carry each arrival decision, across the arrival events' choice lists. */
const ARRIVAL_CHOICES: Record<ArrivalDecision, readonly string[]> = {
  'admit-all': ['admit-all', 'admit'],
  'admit-some': ['admit-some'],
  guests: ['guests'],
  'turn-away': ['turn-away', 'refuse'],
};

function offered(prompt: EventPrompt, ids: readonly string[]): string | null {
  for (const id of ids) if (prompt.choices.some((c) => c.id === id)) return id;
  return null;
}

/** Beds in standing buildings (Section 9). */
export function bedsOf(state: GameState, cfg: Config): number {
  const registry = registryFor(cfg);
  let total = 0;
  for (const b of sortedValues(state.buildings))
    if (b.condition > 0) total += registry.buildings[b.type].beds;
  return total;
}

function candidatesOf(state: GameState, prompt: EventPrompt): Person[] {
  const group = prompt.groupId === null ? undefined : state.arrivalGroups[prompt.groupId];
  return group === undefined ? [] : group.candidates;
}

/** Section 14 and 18: the migration policy, and a capacity check under "ask each time". */
function arrivalDecision(
  state: GameState,
  prompt: EventPrompt,
  cfg: Config,
): { decision: ArrivalDecision; personIds: PersonId[] } {
  const candidates = candidatesOf(state, prompt);
  const ids = candidates.length > 0 ? candidates.map((c) => c.id) : prompt.subjectIds.slice();
  const size = ids.length;
  const pop = Object.keys(state.people).length;
  const all = { decision: 'admit-all' as const, personIds: ids };
  const none = { decision: 'turn-away' as const, personIds: [] };
  if (pop + size > cfg.migration.populationCap) return none;
  const gatesClosed =
    state.counters.gatesClosedUntilTurn !== null &&
    state.counters.gatesClosedUntilTurn > state.turn;
  if (gatesClosed) return none;
  switch (state.policies.migration) {
    case 'open':
      return all;
    case 'closed':
      return none;
    case 'skilled-only': {
      const group = prompt.groupId === null ? undefined : state.arrivalGroups[prompt.groupId];
      const skilled = ids.filter((id) =>
        Object.values(group?.claimedSkills[id] ?? {}).some(
          (level) => level >= cfg.migration.rareSkillMin,
        ),
      );
      return skilled.length === 0 ? none : { decision: 'admit-some', personIds: skilled };
    }
    case 'families-only':
      return candidates.some((c) => isChild(cfg, c)) ? all : none;
    case 'ask': {
      // M7: past the growth the Section 20 population band allows, turn newcomers away (the
      // population must stay within +/-15% of its level 12 seasons ago to count as steady).
      const series = state.series.population;
      const back = cfg.steady.populationBandSeasons;
      const reference = series[Math.max(0, series.length - 1 - back)] ?? pop;
      // ... unless the settlement has no fertile couple left (Section 20: that loses the run).
      if (pop + size > reference * (1 + cfg.steward.admitGrowthShare) && fertileRemain(state, cfg))
        return none;
      const food = state.stocks.food.amount;
      const perSeason = (pop + size) * cfg.needs.foodAdult;
      const foodOk = food >= perSeason * cfg.steward.admitFoodSeasons;
      const spare = bedsOf(state, cfg) - pop;
      if (foodOk && spare >= size) return all;
      const guestFood = size * cfg.migration.guestRation * cfg.migration.guestSeasons;
      if (food >= pop * cfg.needs.foodAdult + guestFood)
        return { decision: 'guests', personIds: ids };
      return none;
    }
  }
}

/** Section 20: a woman of child-bearing age and a man of pairing age remain (the loss test). */
function fertileRemain(state: GameState, cfg: Config): boolean {
  const l = cfg.lifecycle;
  const years = (p: Person) => p.ageSeasons / cfg.people.seasonsPerYear;
  const people = Object.values(state.people);
  return (
    people.some(
      (p) =>
        p.sex === 'female' &&
        years(p) >= l.conceptionMinAgeYears &&
        years(p) <= l.conceptionMaxAgeYears,
    ) && people.some((p) => p.sex === 'male' && years(p) >= l.conceptionMinAgeYears)
  );
}

function eventAnswer(state: GameState, prompt: EventPrompt, cfg: Config): PromptAnswer {
  if (prompt.groupId !== null) {
    const { decision, personIds } = arrivalDecision(state, prompt, cfg);
    const choiceId = offered(prompt, ARRIVAL_CHOICES[decision]);
    if (choiceId !== null) return { kind: 'event', choiceId, personIds, arrival: decision };
    return { kind: 'event', choiceId: prompt.defaultChoiceId, personIds: [] };
  }
  let preferred: readonly string[] = PREFERRED[prompt.eventId] ?? [];
  if (prompt.eventId === 'dispute')
    preferred =
      settlementTrust(state) >= cfg.events.dispute.mediateMinTrust ? ['mediate'] : ['side-a'];
  if (prompt.eventId === 'raid') {
    const guards = peopleSorted(state).filter((p) => p.job === 'guard' && isWorking(p)).length;
    preferred = guards >= cfg.buildings.palisade.guardsRequired ? ['fight'] : ['pay'];
  }
  if (prompt.eventId === 'stranger' || prompt.eventId === 'travellers') {
    const pop = Object.keys(state.people).length;
    const room =
      bedsOf(state, cfg) - pop >= prompt.subjectIds.length && pop < cfg.migration.populationCap;
    preferred = room ? ['admit'] : ['refuse', 'turn-away'];
  }
  const choiceId = offered(prompt, preferred) ?? prompt.defaultChoiceId;
  return { kind: 'event', choiceId, personIds: [] };
}

/**
 * Section 18 "keep unless starvation": eat seed only when it may save a life this season:
 * someone already hungry, weak in health (at most `steward.starvationHealth`) and left with at
 * most `steward.starvationShare` of their need. Eating the seed for a hungry but not dying
 * settlement trades next year's harvest for a few days' food (M7).
 */
function starving(state: GameState, prompt: ShortagePrompt, cfg: Config): boolean {
  for (const [id, share] of Object.entries(prompt.proposed) as [PersonId, number][]) {
    if ((prompt.demand[id] ?? 0) <= 0) continue;
    const p = state.people[id];
    if (p === undefined || p.streaks.hungry === 0) continue;
    if (share <= cfg.steward.starvationShare && p.health <= cfg.steward.starvationHealth)
      return true;
  }
  return false;
}

/** The default answer to any prompt (also used for pausing prompts in headless runs). */
export function stewardAnswer(
  state: GameState,
  prompt: Prompt,
  cfg: Config = config,
): PromptAnswer {
  switch (prompt.kind) {
    case 'shortage': {
      const eatSeed =
        prompt.resource === 'food' && prompt.seedAvailable > 0 && starving(state, prompt, cfg)
          ? Math.min(prompt.seedAvailable, prompt.shortfall)
          : 0;
      // Empty shares: everyone gets the policy's proposal (re-run with any seed eaten).
      return { kind: 'shortage', shares: {}, eatSeed, saveAsOrder: null };
    }
    case 'event':
      return eventAnswer(state, prompt, cfg);
    case 'justice':
      return { kind: 'justice', response: prompt.proposed };
    case 'pairing':
      return { kind: 'pairing', approve: true };
  }
}
